import { useEffect, useRef, useState } from "react";
import BreadcrumbPanel from "../../../../../shared/components/breadcrumb/BreadcrumbPanel";
import { Card, CardContent } from "../../../../../shared/components/card/Card";
import TacticLinesPerformanceFilters from "./TacticLinesPerformanceFilters";
import TacticLinesPerformanceTable from "./TacticLinesPerformanceTable";
import { useTracking } from "react-tracking";
import { DATA_EXPLORERS } from "../../../../../shared/lib/routesResolver";
import {
  TACTIC_LINES_PERFORMANCE_FILTER_SELECTABLES,
  TACTIC_LINES_PERFORMANCE_KPIS,
} from "../../../../../shared/lib/apiEndpointsResolver";
import useAxios from "../../../../../hooks/useAxios";
import {
  catchFunction,
  getCurrentMonthRange,
} from "../../../../../shared/lib/utils";
import useLoading from "../../../../../shared/components/loading/useLoading";

const TacticLinesPerformanceContainer = () => {
  const axios = useAxios();

  const { setLoading } = useLoading();
  const [tacticLines, setTacticLines] = useState([]);
  const tacticLinesRef = useRef([]);
  const [filterSelectables, setFilterSelectables] = useState();
  const defaultDateValue = getCurrentMonthRange();
  const [params, setParams] = useState({
    dateRange: defaultDateValue,
    channels: [],
    platforms: [],
    strategies: [],
    clients: [],
    campaigns: [],
    markets: [],
    categories: [],
    products: [],
    tacticLineName: null,
  });
  const { trackEvent } = useTracking();

  const breadcrumbItems = [
    {
      title: "tacticLinesEfficiency.breadcrumb.first",
      link: DATA_EXPLORERS,
    },
    { title: "tacticLinesEfficiency.breadcrumb.second" },
  ];

  const findFilterSelectables = () => {
    axios
      .get(TACTIC_LINES_PERFORMANCE_FILTER_SELECTABLES)
      .then((response) =>
        setFilterSelectables({
          ...response.data,
          currentStatuses: [
            { label: "DELETED", value: "DELETED" },
            { label: "ACTIVATED", value: "ACTIVATED" },
            { label: "DEACTIVATED", value: "DEACTIVATED" },
            { label: "Unknown", value: "Unknown" },
          ],
        }),
      )
      .catch(catchFunction);
  };

  const findTacticLinesKpis = () => {
    setLoading(true);
    axios
      .post(TACTIC_LINES_PERFORMANCE_KPIS, params)
      .then((response) => {
        setTacticLines(response.data);
        tacticLinesRef.current = response.data;
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const updateParams = (key, value) => {
    setParams({ ...params, [key]: value });
  };

  const updateClientParams = (value) => {
    if (value.length === 0) {
      setParams({
        ...params,
        clients: [],
        campaigns: [],
        markets: [],
        categories: [],
        products: [],
      });
    } else {
      setParams({ ...params, clients: value });
    }
  };

  const onTacticLineNameFilter = (changeEvent) => {
    setTacticLines(
      tacticLinesRef.current.filter((x) =>
        x.tacticLineName
          .toLowerCase()
          .includes(changeEvent.target.value.toLowerCase()),
      ),
    );
  };

  const onCurrentStatusFilter = (value) => {
    if (value.length === 0) {
      setTacticLines(tacticLinesRef.current);
    } else {
      setTacticLines(
        tacticLinesRef.current.filter((x) =>
          value.includes(x.tacticLineCurrentStatus),
        ),
      );
    }
  };

  useEffect(() => {
    findFilterSelectables();
    trackEvent({
      page: "tactic-lines-performance",
      event: "page-load",
    });
  }, []);

  useEffect(() => {
    findTacticLinesKpis();
  }, [params]);

  return (
    <>
      <div className="px-4 pb-4 sm:px-6">
        <BreadcrumbPanel items={breadcrumbItems} />
      </div>
      <div className="flex flex-col flex-1 items-start gap-4 p-4 sm:px-6 sm:py-0 md:gap-8 mt-4">
        <TacticLinesPerformanceFilters
          selectables={filterSelectables}
          defaultDateValue={defaultDateValue}
          onDateChangeValue={(value) => updateParams("dateRange", value)}
          onStrategyChangeValue={(value) => updateParams("strategies", value)}
          onChannelChangeValue={(value) => updateParams("channels", value)}
          onPlatformChangeValue={(value) => updateParams("platforms", value)}
          onClientChangeValue={(value) => updateClientParams(value)}
          onCampaignChangeValue={(value) => updateParams("campaigns", value)}
          onMarketChangeValue={(value) => updateParams("markets", value)}
          onCategoryChangeValue={(value) => updateParams("categories", value)}
          onProductChangeValue={(value) => updateParams("products", value)}
          onTacticLinesNameChangeValue={onTacticLineNameFilter}
          onCurrentStatusChangeValue={onCurrentStatusFilter}
        />
        <Card className="pt-4 w-full">
          <CardContent>
            <TacticLinesPerformanceTable data={tacticLines} />
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default TacticLinesPerformanceContainer;
